import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Skull, ArrowLeft, Loader2, Check, X, Clock, Trophy, Share2, Globe, Users, Flag } from "lucide-react";
import { toast } from "sonner";

const useCountdown = (iso) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!iso) return null;
  const diff = Math.max(0, new Date(iso).getTime() - now);
  const d = Math.floor(diff / 86_400_000);
  const h = Math.floor((diff % 86_400_000) / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  const s = Math.floor((diff % 60_000) / 1_000);
  return { d, h, m, s, done: diff === 0, totalMs: diff };
};

const NightPlay = () => {
  const { signupId } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [guess, setGuess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);
  const [tab, setTab] = useState("global");

  const load = async () => {
    try {
      const { data } = await api.get(`/night/play/${signupId}`);
      setState({ loading: false, data, error: "" });
    } catch (err) {
      setState({ loading: false, data: null, error: err?.response?.data?.detail || "Could not load the event." });
    }
  };
  const loadLeaderboard = async () => {
    try {
      const { data } = await api.get("/night/leaderboard");
      setLeaderboard(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    loadLeaderboard();
    // Poll every 10s so clues reveal in real time + leaderboard updates
    const t = setInterval(() => { load(); loadLeaderboard(); }, 10_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupId]);

  const waveStart = state.data?.wave?.starts_at_utc;
  const countdown = useCountdown(waveStart);
  const waveLive = useMemo(() => {
    if (!waveStart) return false;
    return new Date(waveStart).getTime() <= Date.now();
  }, [waveStart]);

  const submitGuess = async () => {
    if (!guess) { toast.error("Pick a suspect."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/night/play/${signupId}/guess`, {
        wave_id: state.data.wave.id, guess,
      });
      if (data.correct) toast.success(`Solved — ${data.score} pts`);
      else toast.message("Not quite.", { description: "The real culprit is in the reveal below." });
      await load(); await loadLeaderboard();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit guess.");
    } finally {
      setSubmitting(false);
    }
  };

  const shareResult = async () => {
    const url = `${window.location.origin}/api/night/play/${signupId}/result-card.png`;
    const text = `I just played wasntme.party Global Mystery Night. Join the next wave:`;
    const nightUrl = `${window.location.origin}/night`;
    if (navigator.share) {
      try { await navigator.share({ title: "Global Mystery Night", text, url: nightUrl }); }
      catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(`${text} ${nightUrl}`);
      toast.success("Share link copied.");
    }
    api.post("/events", { event: "gmn_result_shared" }).catch(() => {});
  };

  if (state.loading) {
    return <div className="grain min-h-screen flex items-center justify-center text-[#a39797]"><Loader2 className="animate-spin" /></div>;
  }
  if (state.error) {
    return (
      <div className="grain min-h-screen flex items-center justify-center p-6">
        <div className="card-noir p-6 max-w-md" data-testid="night-play-error">
          <div className="text-[#ff6b6b] text-sm mb-3">{state.error}</div>
          <Link to="/night" className="btn-ghost text-sm"><ArrowLeft size={14} /> back</Link>
        </div>
      </div>
    );
  }

  const d = state.data;
  const accent = d.event.accent_hex || "#8b0000";
  const totalClues = d.total_clues || 0;
  const visibleCount = d.clues_visible?.length || 0;
  const hasGuessed = !!d.my_guess;
  const correct = d.my_guess?.correct;
  const nextClue = d.wave && d.case_setup && visibleCount < totalClues;

  return (
    <div className="grain min-h-screen" data-testid="night-play-page">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link to="/night" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] mb-10">
          <ArrowLeft size={16} /> back to the event page
        </Link>

        <div className="font-hand rotate-[-1deg] text-2xl mb-2" style={{ color: accent }}>
          detective: {d.signup.nickname}{d.signup.country ? ` · ${d.signup.country}` : ""}{d.signup.crew ? ` · ${d.signup.crew}` : ""}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mb-2">{d.event.title}</h1>
        {d.wave && (
          <div className="text-[#a39797] mb-6">
            <Clock size={14} className="inline mr-1" /> {d.wave.name}
          </div>
        )}

        {/* Pre-wave countdown */}
        {!waveLive && countdown && !countdown.done && (
          <div className="card-noir p-6 mb-6" style={{ borderColor: accent }} data-testid="night-play-pre-countdown">
            <div className="text-xs tracking-widest text-[#a39797] mb-2">YOUR WAVE OPENS IN</div>
            <div className="flex items-center gap-5">
              {["d", "h", "m", "s"].map((k) => (
                <div key={k} className="text-center">
                  <div className="font-display text-3xl text-[#f4f0ec]">{String(countdown[k]).padStart(2, "0")}</div>
                  <div className="text-[10px] tracking-widest text-[#a39797] uppercase">
                    {{ d: "days", h: "hrs", m: "min", s: "sec" }[k]}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-xs text-[#a39797] mt-3">Come back here when the countdown hits zero. Clues drop every few minutes.</div>
          </div>
        )}

        {/* Live play — setup + suspects + revealed clues + guess form */}
        {waveLive && d.case_setup && (
          <>
            <div className="card-noir p-6 mb-6">
              <div className="text-xs tracking-widest text-[#a39797] mb-2">THE CASE</div>
              <p className="text-[#c7bebe] leading-relaxed">{d.case_setup}</p>
            </div>

            <div className="mb-6">
              <div className="text-xs tracking-widest text-[#a39797] mb-3">SUSPECTS</div>
              <div className="grid md:grid-cols-3 gap-3">
                {d.suspects.map((s) => (
                  <div key={s.name} className="card-noir p-4" data-testid={`suspect-${s.name}`}>
                    <div className="font-display text-xl mb-1">{s.name}</div>
                    <div className="text-xs text-[#a39797]">{s.profile}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <div className="text-xs tracking-widest text-[#a39797] mb-3 flex items-center justify-between">
                <span>CLUES — {visibleCount} of {totalClues} revealed</span>
                {nextClue && <span className="font-hand text-[#d4af37]">next clue in a few</span>}
              </div>
              <div className="space-y-2">
                {(d.clues_visible || []).map((c) => (
                  <div key={c.index} className="card-noir p-4" data-testid={`clue-${c.index}`}>
                    <div className="flex items-start gap-3">
                      <div className="font-display text-xl" style={{ color: accent }}>{c.index + 1}.</div>
                      <div className="text-[#f4f0ec]">{c.text}</div>
                    </div>
                  </div>
                ))}
                {Array.from({ length: totalClues - visibleCount }).map((_, i) => (
                  <div key={`unrev-${i}`} className="card-noir p-4 opacity-40 border-dashed">
                    <div className="flex items-start gap-3">
                      <div className="font-display text-xl text-[#a39797]">{visibleCount + i + 1}.</div>
                      <div className="text-[#a39797] italic">Hidden until the next clue drops.</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {!hasGuessed ? (
              <div className="card-noir p-5" style={{ borderColor: accent }}>
                <div className="text-xs tracking-widest text-[#a39797] mb-3">
                  WHO DID IT?  <span className="font-hand text-[#d4af37] ml-2">Earlier correct guesses score more.</span>
                </div>
                <div className="grid md:grid-cols-3 gap-2 mb-3">
                  {d.suspects.map((s) => (
                    <button key={s.name} onClick={() => setGuess(s.name)}
                      data-testid={`pick-${s.name}`}
                      className={`card-noir p-3 transition-colors ${guess === s.name ? "" : "hover:border-[#8b0000]"}`}
                      style={{ borderColor: guess === s.name ? accent : undefined, backgroundColor: guess === s.name ? accent + "15" : undefined }}>
                      <div className="font-display text-lg">{s.name}</div>
                    </button>
                  ))}
                </div>
                <button onClick={submitGuess} disabled={submitting || !guess}
                  className="btn-primary disabled:opacity-50" data-testid="submit-guess"
                  style={{ backgroundColor: accent }}>
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Skull size={16} />}
                  {submitting ? "submitting…" : "lock in my guess"}
                </button>
              </div>
            ) : (
              <div className="card-noir p-6" style={{ borderColor: correct ? "#4ade80" : "#ff6b6b" }} data-testid="night-play-result">
                <div className="flex items-center gap-3 mb-2">
                  {correct ? <Check size={22} className="text-[#4ade80]" /> : <X size={22} className="text-[#ff6b6b]" />}
                  <div className="font-display text-2xl">
                    {correct ? `You got it — ${d.my_guess.score} pts` : `Not quite — your guess was ${d.my_guess.guess}`}
                  </div>
                </div>
                {d.reveal && (
                  <>
                    <div className="mt-3 text-xs tracking-widest text-[#a39797]">THE CULPRIT</div>
                    <div className="font-display text-xl mb-2" style={{ color: accent }}>{d.reveal.culprit}</div>
                    <p className="text-[#c7bebe] leading-relaxed">{d.reveal.explanation}</p>
                  </>
                )}
                <div className="mt-5 flex items-center gap-2 flex-wrap">
                  <button onClick={shareResult} className="btn-ghost text-sm" data-testid="btn-share-result">
                    <Share2 size={14} /> Share my result
                  </button>
                  <a href={`/api/night/play/${signupId}/result-card.png`} target="_blank" rel="noreferrer"
                    className="btn-ghost text-sm" data-testid="btn-download-result">
                    Download card
                  </a>
                </div>
              </div>
            )}
          </>
        )}

        {/* Leaderboards */}
        {leaderboard && (leaderboard.global?.length > 0 || leaderboard.by_country?.length > 0) && (
          <div className="mt-10">
            <div className="font-hand text-[#d4af37] rotate-[-1deg] text-2xl mb-2">leaderboard</div>
            <h2 className="font-display text-2xl mb-4">
              <Trophy size={20} className="inline mr-2 -mt-1" style={{ color: accent }} />
              {leaderboard.total_players} detective{leaderboard.total_players === 1 ? "" : "s"} in
            </h2>
            <div className="flex items-center gap-2 mb-4">
              <button onClick={() => setTab("global")}
                className={`btn-ghost text-xs ${tab === "global" ? "border-[#8b0000]" : ""}`}
                data-testid="tab-global">
                <Globe size={12} /> Global
              </button>
              <button onClick={() => setTab("country")}
                className={`btn-ghost text-xs ${tab === "country" ? "border-[#8b0000]" : ""}`}
                data-testid="tab-country">
                <Flag size={12} /> Countries
              </button>
              <button onClick={() => setTab("crew")}
                className={`btn-ghost text-xs ${tab === "crew" ? "border-[#8b0000]" : ""}`}
                data-testid="tab-crew">
                <Users size={12} /> Crews
              </button>
            </div>
            {tab === "global" && (
              <div className="space-y-1" data-testid="board-global">
                {leaderboard.global.slice(0, 20).map((r, i) => (
                  <div key={i} className="card-noir p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="font-display text-xl w-10 text-right text-[#a39797]">#{i + 1}</div>
                      <div>
                        <div className="font-display text-lg">{r.nickname}</div>
                        <div className="text-xs text-[#a39797]">
                          {r.country && <>{r.country}</>}{r.country && r.crew && " · "}{r.crew}
                        </div>
                      </div>
                    </div>
                    <div className="font-display text-xl" style={{ color: accent }}>{r.score.toLocaleString()}</div>
                  </div>
                ))}
                {leaderboard.global.length === 0 && (
                  <div className="text-[#a39797] text-sm">No scores yet. Be the first.</div>
                )}
              </div>
            )}
            {tab === "country" && (
              <div className="space-y-1" data-testid="board-country">
                {leaderboard.by_country.map((r, i) => (
                  <div key={r.country} className="card-noir p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="font-display text-xl w-10 text-right text-[#a39797]">#{i + 1}</div>
                      <div>
                        <div className="font-display text-lg">{r.country}</div>
                        <div className="text-xs text-[#a39797]">{r.player_count} detective{r.player_count === 1 ? "" : "s"}</div>
                      </div>
                    </div>
                    <div className="font-display text-xl" style={{ color: accent }}>{r.score.toLocaleString()}</div>
                  </div>
                ))}
                {leaderboard.by_country.length === 0 && (
                  <div className="text-[#a39797] text-sm">No country scores yet.</div>
                )}
              </div>
            )}
            {tab === "crew" && (
              <div className="space-y-1" data-testid="board-crew">
                {leaderboard.by_crew.map((r, i) => (
                  <div key={r.crew} className="card-noir p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="font-display text-xl w-10 text-right text-[#a39797]">#{i + 1}</div>
                      <div>
                        <div className="font-display text-lg">{r.crew}</div>
                        <div className="text-xs text-[#a39797]">{r.player_count} detective{r.player_count === 1 ? "" : "s"}</div>
                      </div>
                    </div>
                    <div className="font-display text-xl" style={{ color: accent }}>{r.score.toLocaleString()}</div>
                  </div>
                ))}
                {leaderboard.by_crew.length === 0 && (
                  <div className="text-[#a39797] text-sm">No crew scores yet.</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NightPlay;
